import store from '@/store'
import { IntentAction, MonthsAbbreviated } from '@/modules/Enums'

import { MetierLocalStorage } from 'modulelocalstorage'
import { Utils } from 'moduleutils'
import { Initialisation } from '@/modules/Initialisation'
import i18n from '@/lang'
import dayjs from 'dayjs'
import dateFormat from 'dateformat'

/**
 * Retourne le montant formaté
 * @param amount
 * @param currency
 * @returns {string}
 */
export function $amount (amount = null, currency = null) {
  return Utils.formatCurrencyAmount(
    amount !== null ? amount : store.state.amount,
    currency !== null ? currency : store.getters.currency
  )
}

/**
 * Affiche une alerte
 *
 * @param text
 * @param type
 * @param textSup
 * @param duration
 * @param buttons
 * @param callback
 * @param useDefaultButton
 */
export function $alert (
  text, type = 'error', textSup = '', duration = 0,
  buttons = [], callback = null, useDefaultButton = true
) {
  // Si on vient d'un appel externe (App2App), et qu'il s'agit d'une erreur, on renvoie l'erreur
  if (store.getters.externalAction && text && type === 'error') {
    CissAndroid.sendError({ message: (text + '\n' + textSup) })
    return
  }

  let alert = false
  if (text) {
    alert = {
      text: text,
      type: type,
      textSup: textSup,
      duration: duration,
      buttons: buttons,
      callback: callback,
      useDefaultButton: useDefaultButton
    }
  }

  let event = null
  if (document.createEvent) {
    event = document.createEvent('HTMLEvents')
    event.initEvent('alert', true, true)
    event.eventName = 'alert'
    event.alert = alert
    document.dispatchEvent(event)
  } else {
    event = document.createEventObject()
    event.eventName = 'alert'
    event.eventType = 'alert'
    event.alert = alert
    document.fireEvent('on' + event.eventType, event)
  }
}

/**
 * Ferme le clavier virtuel
 * @param nextTick
 */
export function $closeKeyboard (nextTick) {
  if (window.Keyboard && Keyboard.hide) {
    nextTick(() => {
      Keyboard.hide()
      StatusBar.hide()
    })
  }
}

/**
 * Scroll to element
 * @param el
 * @param ref
 */
export function $scrollTo (el, ref) {
  document.getElementById(el).classList.add('dialog-fullscreen-top')

  this.$nextTick(() => {
    let element = this.$refs[ref].$el

    element.scrollIntoView({
      block: 'start',
      behavior: 'smooth',
      inline: 'nearest'
    })
  })
}

/**
 * Blur scroll
 * @param el
 */
export function $blurScroll (el) {
  document.getElementById(el).classList.remove('dialog-fullscreen-top')
}

/**
 * Blur ripple
 * @param el
 */
export function $blurRipple (el) {
  if (!IS_IOS) return
  setTimeout(() => {
    const els = el.getElementsByClassName('v-ripple__container')
    if (els.length > 0) {
      els[0].remove()
    }
  }, 300)
}

export function $bluetoothPrint (printer, text, success = null, error = null) {
  // Connexion à l'imprimante bluetooth
  BTPrinter.connect(
    () => {
      // Impression du text
      BTPrinter.printText(
        () => {
          // Attente avant déconnexion
          setTimeout(() => {
            BTPrinter.disconnect(
              () => {
                if (success !== null) success()
              },
              (err) => {
                $alert(err)
                if (error !== null) error(err)
              },
              printer
            )
          }, 1800)
        },
        (err) => {
          $alert(err)
          if (error !== null) error(err)
        },
        text
      )
    },
    (err) => {
      $alert(err)
      if (error !== null) error(err)
    },
    printer
  )
}

/**
 * Retourne la date formatée
 * @param date
 * @param long
 * @returns {string}
 */
export function $formatDate (date = null, long = false) {
  let d = date ? date : new Date()
  if (long) {
    return [
      d.getDate().padLeft(),
      MonthsAbbreviated[d.getMonth()],
      d.getFullYear()
    ].join(' ')
  } else {
    return dateFormat(date, 'dd/mm/yy')
  }
}

/**
 * Retourne l'heure formatée
 * @param date
 * @returns {string}
 */
export function $formatTime (date = null) {
  let d = date ? date : new Date()
  return [
    d.getHours().padLeft(),
    d.getMinutes().padLeft(),
    d.getSeconds().padLeft()
  ].join(':')
}

/**
 * Retourne un nombre d'espaces
 * @param nb
 * @returns {string}
 */
export function getSpaces (nb) {
  return ''.padStart(nb, ' ')
}

/**
 * Générère une référence de transaction
 *
 * @returns {string}
 */
export function createReference (gap = 0) {
  return ('000000' + MetierLocalStorage.getIdPointDeVente()).slice(-6) +
    ('000' + MetierLocalStorage.getRang()).slice(-3) +
    (Math.round(Date.now() / 1000) + gap)
}

/**
 * Programme un téléparamétrage dans x minutes
 *
 * @param time
 */
export function startRemoteSettingsScheduler (time) {
  // eslint-disable-next-line no-console
  console.info(`StartRemoteSettingsScheduler to ${time} minutes (${(time/60).toFixed(2)} hours)`)

  setTimeout(() => {
    SchedulerPlugin.configure(
      () => {
        startRemoteSettings(true)

        SchedulerPlugin.finish();
      },
      (error) => {
        $alert(i18n.t('error.remote_auto', { error: error }))
      },
      { minimumFetchInterval: time * 60000 }
    )
  }, 3000)
}

export function startRemoteSettings (startedByScheduler = false) {
  const date = new Date()
  // eslint-disable-next-line no-console
  console.info(`ReloadRemoteSettings started at ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`)

  Initialisation.initTeleparametrage(
    (e) => {},
    startedByScheduler
  )
}

/**
 * Retourne le nombre d'heures restant
 *
 * @returns {number}
 */
export function getTimeRemaining () {
  const remoteSettingsInfos = MetierLocalStorage.getRemoteSettingsAuto()

  const date = new Date()
  const currentTime = (date.getHours() * 60) + date.getMinutes()

  // Calcul du temps restant avant le prochain téléparématrage auto
  let time = (
    (parseInt(remoteSettingsInfos.time.substring(0, 2)) * 60)
    + parseInt(remoteSettingsInfos.time.substring(2))
  ) - currentTime

  // Si l'heure est dépassée, on ajoute 24H au temps négatif
  if (time < 0) {
    time += (24 * 60)
  }

  // Ajout d'un nombre de minutes aléatoire d'un maximum de 5
  time += Math.floor(Math.random() * 5)

  return time
}

/**
 * Retourne l'orientation suivante en fonction de l'orientation courante
 *
 * @returns {string}
 */
export function getNextOrientation () {
  const currentOrientation = MetierLocalStorage.getScreenOrientation()
  switch (currentOrientation) {
    case 'portrait-primary':
      return 'landscape-primary'
    case 'landscape-primary':
      return 'portrait-secondary'
    case 'portrait-secondary':
      return 'landscape-secondary'
    case 'landscape-secondary':
      return 'portrait-primary'
  }
}

export function getRandomRank () {
  const rand = parseInt(Date.now().toString().slice(-3))
  if (rand < 10 || rand > 990) {
    return getRandomRank()
  }
  return rand
}

/**
 * Stop les éventuels services en fonctionnement
 */
export function stopServices () {
  if (MetierLocalStorage.getSansContact()) {
    Cashless.clear()
  }

  if (IS_ANDROID) {
    SchedulerPlugin.stop()
    CissAndroid.forgotLogins()
  }
}

export function getIntent () {
  return new Promise((resolve) => {
    CissAndroid.getIntent((intent) => {
      if (intent && intent.intentAction !== IntentAction.MAIN) {
        store.commit('intent', intent)
      }
      resolve(intent)
    })
  })
}

/**
 * Vérifie si l'on doit afficher la campagne
 * @returns {boolean}
 */
export function needToAskDonation () {
  return needToAskCampaign(store.getters.tipCampaign)
}
export function needToAskTip () {
  return needToAskCampaign(store.getters.donationCampaign)
}

function needToAskCampaign (campagne) {
  if (!campagne) return false
  const campaignDeactivated = store.getters.campaignDeactivated

  const now = dayjs()
  const timeNow = dateFormat(now, 'HHMMss')
  const timeNowNoSeconds = dateFormat(now, 'HHMM')

  const amount = store.state.amount;

  const plage1 = MetierLocalStorage.getPlageArretCampagne1()
  const plage2 = MetierLocalStorage.getPlageArretCampagne2()

  return (campaignDeactivated === null || now > campaignDeactivated) && // on vérifie que la campagne n'est pas désactivé
    (
      (plage1 === null && plage2 === null && // plages configurées dans le tpe (il y en a pas)
        (
          (campagne.PlageArretDebut === null && campagne.PlageArretFin === null) || // pas de plage d'arret configuré dans le teleparam
          (timeNow < campagne.PlageArretDebut.replaceAll(':', '') || timeNow >= campagne.PlageArretFin.replaceAll(':', '')) // si configuré dans téléparam on vérifie les horaires
        )
      ) ||
      (
        (
          plage1 === null ||
          timeNowNoSeconds < plage1.debut.replace(':', '') ||
          timeNowNoSeconds >= plage1.fin.replace(':', '')
        ) &&
        (
          plage2 === null ||
          timeNowNoSeconds < plage2.debut.replace(':', '') ||
          timeNowNoSeconds >= plage2.fin.replace(':', '')
        )
      )
    ) &&
    dayjs(campagne.DateDebut, 'YYYY-MM-DD HH:mm:ss') <= now && // On vérifie la date de début de la campagne.
    (campagne.DateFin === null || dayjs(campagne.DateFin, 'YYYY-MM-DD HH:mm:ss') > now) &&
    amount >= campagne.MontantMinimum && // On vérifie le montant min entrée dans le tpe
    (
      campagne.MontantMinimum !== 0 || (campagne.MontantMinimum === null || amount >= campagne.MontantMinimum)
    ) &&
    (campagne.MontantMaximum === null || amount <= campagne.MontantMaximum)
}
